import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, Subject, concatMap, from, retry, throwError, timer } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { TranslationResult } from '@projects/web-rtc-communication/src/app/services/audio-translator.service';

interface ConversationSummary {
  uuid: string;
  status: string;
}

interface WrappedResponse<T> {
  success: boolean;
  data: T;
}

/** One saved call as listed on the Conversazioni page. */
export interface ArchivedConversation {
  uuid: string;
  status: string;
  room_name: string;
  technician_name: string;
  operator_name: string;
  started_at: string;
  ended_at: string | null;
  last_activity_at: string;
  day: string;
  message_count: number;
  languages: string[];
  document_uuid: string | null;
  rag_status: string | null;
  technician_id?: number | null;
  company_id?: number | null;
  company_name?: string;
}

export interface ArchivedMessage {
  is_local: boolean;
  text: string;
  translation: string | null;
  source_language: string;
  target_language: string;
  captured_at: string;
  // 'chat' = typed to the field technician during the call
  kind?: 'speech' | 'chat';
}

export interface ArchivedConversationDetail extends ArchivedConversation {
  messages: ArchivedMessage[];
}

type ArchiveCommand =
  | { type: 'room'; roomUuid: string }
  | { type: 'entry'; result: TranslationResult; capturedAt: string }
  | { type: 'finalize' };

/** Persists translated phrases in order and closes the archive only after the queue is drained. */
@Injectable({ providedIn: 'root' })
export class ConversationArchiveService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.api.base_url}/videochat/conversations`;
  private readonly commands = new Subject<ArchiveCommand>();
  // Room of the open call page; roomUuid below is the room the queue is working on, which changes only after the
  // phrases and the closing of the previous room have been sent
  private activeRoomUuid: string | null = null;
  private roomUuid: string | null = null;
  private conversationUuid: string | null = null;
  private resumeUuid: string | null = null;

  constructor() {
    this.commands.pipe(
      concatMap(command => from(this.execute(command)).pipe(
        catchError(error => {
          console.error('Archiviazione conversazione non riuscita', error);
          return EMPTY;
        })
      ))
    ).subscribe();
  }

  setRoom(roomUuid: string): void {
    this.activeRoomUuid = roomUuid;
    this.commands.next({ type: 'room', roomUuid });
  }

  record(result: TranslationResult): void {
    if (!this.activeRoomUuid || !result.text?.trim()) {
      return;
    }
    this.commands.next({ type: 'entry', result, capturedAt: new Date().toISOString() });
  }

  finalize(): void {
    this.commands.next({ type: 'finalize' });
  }

  getArchive(): Observable<ArchivedConversation[]> {
    return this.http.get<WrappedResponse<ArchivedConversation[]>>(`${this.baseUrl}/archive`).pipe(
      map(response => response?.data ?? [])
    );
  }

  getConversation(uuid: string): Observable<ArchivedConversationDetail> {
    return this.http.get<WrappedResponse<ArchivedConversationDetail>>(`${this.baseUrl}/${uuid}`).pipe(
      map(response => response.data)
    );
  }

  private async execute(command: ArchiveCommand): Promise<void> {
    if (command.type === 'room') {
      this.switchRoom(command.roomUuid);
      return;
    }
    if (!this.roomUuid) {
      return;
    }
    if (command.type === 'finalize') {
      await this.finalizeCurrent();
      return;
    }

    const result = command.result;
    const body = {
      client_message_id: this.newUuid(),
      is_local: result.isLocal,
      text: result.text,
      translation: result.translation,
      source_language: result.sourceLanguage,
      target_language: result.targetLanguage,
      kind: result.kind ?? 'speech',
      captured_at: command.capturedAt,
    };
    try {
      await this.postMessage(await this.ensureConversation(), body);
    } catch (error) {
      if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
        throw error;
      }
      // 409: the conversation was closed meanwhile (5 minutes of silence, or its PDF being written). The call goes
      // on, so the phrase opens a new conversation instead of being lost together with all the next ones.
      this.forgetConversation();
      await this.postMessage(await this.ensureConversation(), body);
    }
  }

  private postMessage(conversationUuid: string, body: object): Promise<unknown> {
    // retry() would repeat a 409 too: only server and network errors are worth repeating
    return this.http.post(`${this.baseUrl}/${conversationUuid}/messages`, body).pipe(
      retry({ count: 2, delay: error => error instanceof HttpErrorResponse && error.status === 409 ? throwError(() => error) : timer(0) })
    ).toPromise();
  }

  private forgetConversation(): void {
    if (this.roomUuid) {
      sessionStorage.removeItem(this.storageKey(this.roomUuid));
    }
    this.conversationUuid = null;
    this.resumeUuid = null;
  }

  private switchRoom(roomUuid: string): void {
    if (this.roomUuid === roomUuid) {
      return;
    }
    this.roomUuid = roomUuid;
    this.conversationUuid = null;
    this.resumeUuid = sessionStorage.getItem(this.storageKey(roomUuid));
  }

  private async ensureConversation(): Promise<string> {
    if (!this.roomUuid) {
      throw new Error('Stanza non impostata');
    }
    if (this.conversationUuid) {
      return this.conversationUuid;
    }

    const response = await this.http.post<WrappedResponse<ConversationSummary>>(this.baseUrl, {
      room_uuid: this.roomUuid,
      resume_uuid: this.resumeUuid,
    }).pipe(retry(2)).toPromise();
    const uuid = response?.data?.uuid;
    if (!uuid) {
      throw new Error('Identificativo conversazione mancante');
    }
    this.conversationUuid = uuid;
    this.resumeUuid = uuid;
    sessionStorage.setItem(this.storageKey(this.roomUuid), uuid);
    return uuid;
  }

  private async finalizeCurrent(): Promise<void> {
    if (!this.roomUuid || !this.conversationUuid) {
      return;
    }
    const roomUuid = this.roomUuid;
    const conversationUuid = this.conversationUuid;
    await this.http.post(`${this.baseUrl}/${conversationUuid}/finalize`, {}).pipe(retry(1)).toPromise();
    sessionStorage.removeItem(this.storageKey(roomUuid));
    this.conversationUuid = null;
    this.resumeUuid = null;
  }

  private storageKey(roomUuid: string): string {
    return `xr-call-conversation:${roomUuid}`;
  }

  private newUuid(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, value => {
      const random = Math.floor(Math.random() * 16);
      const nibble = value === 'x' ? random : (random & 0x3) | 0x8;
      return nibble.toString(16);
    });
  }
}
